'use client';

import { styled } from 'styled-components';

import TextLink from '@dvd/components/TextLink';

import { darkHomeTokens } from './tokens';

/**
 * Named text and layout primitives shared across the surface. `Section*` are
 * the inset bands' type; `Band*` are the full-bleed showcase bands', which run
 * centred over gradient artwork rather than left-aligned in a column;
 * `Eyebrow` is the kicker every band shares.
 */

/**
 * Wraps what paints on the canvas — `<main>` and the CTA/footer region — and
 * declares the page's colour scheme once. The promo bar and Navigation sit
 * outside it so they keep the site defaults (custom properties inherit).
 * `color` is restated because DVD resolves it on body. Why: DESIGN.md,
 * “Colour scheme”.
 */
export const SurfaceRoot = styled.div`
  --global-color: ${({ theme }) => theme.colors.white};
  --global-link-color: ${({ theme }) => theme.colors.white};
  --global-hover-color: ${({ theme }) => theme.colors.white};
  --global-visited-color: ${({ theme }) => theme.colors.white};
  color: var(--global-color);
`;

/**
 * Every band's kicker. Deliberately not built on `Styled.Eyebrow`: the DVD
 * primitive runs 1.25rem on desktop where this surface wants its own 1.125rem
 * step, and carries an `& + h1` sibling margin that silently stacked onto the
 * hero's flex gap. Same face, weight, tracking, and case as DVD's — only
 * those two departures.
 */
export const Eyebrow = styled.p`
  color: ${({ theme }) => theme.colors.blue300};
  font-family: var(--font-inter);
  font-size: ${({ theme }) => theme.typography.fontSize.eyebrow};
  font-weight: ${({ theme }) => theme.typography.fontWeight.eyebrow};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.eyebrow};
  line-height: ${({ theme }) => theme.typography.lineHeight.eyebrow};
  margin: 0;
  text-transform: uppercase;

  /* White on the glow bands' gradient artwork. */
  &.light {
    color: ${({ theme }) => theme.colors.white};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${darkHomeTokens.typography.fontSize.desktop.eyebrow};
  }
`;

export const SectionTitle = styled.h2`
  font-family: var(--font-walsheim);
  font-size: ${darkHomeTokens.typography.fontSize.sectionTitle};
  font-weight: ${({ theme }) => theme.typography.fontWeight.headingOne};
  line-height: 1.15;
  /* Global heading margins would stack on the parent flex gap. */
  margin: 0;
  max-width: ${darkHomeTokens.maxWidth.column};
  text-wrap: balance;

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${darkHomeTokens.typography.fontSize.desktop.sectionTitle};
  }
`;

export const SectionLede = styled.p`
  color: ${darkHomeTokens.colors.bodyLight};
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLg};
  line-height: 1.5;
  margin: 0;
  max-width: ${darkHomeTokens.maxWidth.prose};
  text-wrap: pretty;
`;

export const BandTitle = styled.h2`
  font-family: var(--font-walsheim);
  font-size: ${darkHomeTokens.typography.fontSize.sectionTitle};
  font-weight: ${({ theme }) => theme.typography.fontWeight.headingOne};
  line-height: 1.2;
  /* Global heading margins would stack on the parent flex gap. */
  margin: 0 auto;
  max-width: ${darkHomeTokens.maxWidth.band};
  text-wrap: balance;

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${darkHomeTokens.typography.fontSize.desktop.sectionTitle};
  }
`;

export const BandLink = styled(TextLink)`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyMd};

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodyMd};
  }
`;
